/**
 * The sign in screen. A2 owns it.
 *
 * What it deliberately does not do:
 *   - name the product, the version, or the company
 *   - tell anyone whether a handle exists. One failure sentence, always the same one
 *   - in passkey mode, offer a PIN unless this browser already carries an enrolled device
 *     cookie, or any path from a PIN to enrolling a device
 *   - in PIN mode, offer a passkey, an enrollment form, or a handle field at all
 *
 * Two modes, decided by `auth_mode` on the session bootstrap (see features/auth/mode.ts):
 *   passkey   sign in with a passkey, or a PIN on an enrolled device
 *   pin       the PIN alone, nothing else. The server works out who from the PIN.
 *
 * Three entry points:
 *   /login                sign in
 *   /login?enroll=CODE    a new device, brought in by an owner-issued code (passkey mode)
 *   /login?next=/orders   where to land afterwards
 */
import { useEffect, useState } from 'react';
import { createRoute, useSearch } from '@tanstack/react-router';
import { Route as rootRoute } from '../__root';
import { PinPad } from '@/features/auth/PinPad';
import { hasDeviceHint } from '@/features/auth/device';
import { useAuthMode } from '@/features/auth/mode';
import { AuthModeUnreachable } from '@/features/auth/AuthModeUnreachable';
import { loginErrorMessage, passkeyLogin, pinLogin, registerPasskey } from '@/features/auth/api';
import '@/features/auth/auth.css';

interface LoginSearch {
  enroll?: string;
  next?: string;
}

type Mode = 'passkey' | 'pin' | 'enroll';

function landing(next: string | undefined): string {
  // Only same-site paths. An open redirect on a login screen is how a phishing page
  // borrows your domain.
  if (!next || !next.startsWith('/') || next.startsWith('//')) return '/';
  return next;
}

export function LoginScreen() {
  const search = useSearch({ strict: false }) as LoginSearch;
  const enrollCode = typeof search.enroll === 'string' ? search.enroll : undefined;
  const auth = useAuthMode();
  const pinOnly = auth.mode === 'pin';

  const [mode, setMode] = useState<Mode>(enrollCode ? 'enroll' : 'passkey');
  const [handle, setHandle] = useState('');
  const [code, setCode] = useState(enrollCode ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetToken, setResetToken] = useState(0);
  const [deviceKnown, setDeviceKnown] = useState(false);

  useEffect(() => {
    setDeviceKnown(hasDeviceHint());
  }, []);

  useEffect(() => {
    if (enrollCode) {
      setMode('enroll');
      setCode(enrollCode);
    }
  }, [enrollCode]);

  // PIN mode has exactly one screen. Whatever the URL asked for, this is it.
  useEffect(() => {
    if (pinOnly) setMode('pin');
  }, [pinOnly]);

  function done(): void {
    // A full navigation so every cached query is rebuilt under the new session.
    window.location.assign(landing(search.next));
  }

  async function run(work: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await work();
      done();
    } catch (err) {
      setError(loginErrorMessage(err));
      setResetToken((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }

  const showPin = pinOnly || mode === 'pin';

  return (
    <main className="a-auth">
      <div className="a-auth__card">
        <div className="a-auth__mark" aria-hidden="true" />
        <h1 className="a-auth__title">Sign in</h1>

        {error ? (
          <p className="a-error" role="alert">
            {error}
          </p>
        ) : null}

        {auth.unreachable ? <AuthModeUnreachable onRetry={auth.retry} /> : null}
        {!auth.resolved && !auth.unreachable ? <p className="a-auth__hint">One moment.</p> : null}

        {auth.resolved && !pinOnly && mode === 'enroll' ? (
          <>
            <p className="a-auth__hint">
              This device is being set up. Registering a passkey enrolls it and signs you in.
            </p>
            <div className="a-field">
              <label className="a-field__label" htmlFor="enroll-code">
                Enrollment code
              </label>
              <input
                id="enroll-code"
                className="a-input"
                value={code}
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => setCode(event.target.value)}
              />
            </div>
            <button
              type="button"
              className="a-btn a-btn--primary"
              disabled={busy || code.trim().length === 0}
              onClick={() => void run(() => registerPasskey({ enrollmentCode: code.trim() }))}
            >
              {busy ? 'Waiting for the device' : 'Register this device'}
            </button>
            <button type="button" className="a-btn a-btn--quiet" onClick={() => setMode('passkey')}>
              I already have a passkey
            </button>
          </>
        ) : null}

        {auth.resolved && !pinOnly && mode === 'passkey' ? (
          <>
            <button
              type="button"
              className="a-btn a-btn--primary"
              disabled={busy}
              onClick={() => void run(() => passkeyLogin())}
            >
              {busy ? 'Waiting for the device' : 'Continue with passkey'}
            </button>

            {deviceKnown ? (
              <>
                <div className="a-divider">or</div>
                <button type="button" className="a-btn" onClick={() => setMode('pin')} disabled={busy}>
                  Use PIN on this device
                </button>
              </>
            ) : null}

            <p className="a-auth__hint">
              A passkey is the only way to sign in on a device that has not been set up here.
            </p>
          </>
        ) : null}

        {auth.resolved && showPin ? (
          <>
            {pinOnly ? (
              <p className="a-auth__hint">Enter your PIN.</p>
            ) : (
              <div className="a-field">
                <label className="a-field__label" htmlFor="handle">
                  Your handle
                </label>
                <input
                  id="handle"
                  className="a-input"
                  value={handle}
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  onChange={(event) => setHandle(event.target.value)}
                />
              </div>
            )}
            <PinPad
              disabled={busy || (!pinOnly && handle.trim().length === 0)}
              resetToken={resetToken}
              onSubmit={(pin) => void run(() => pinLogin(pinOnly ? { pin } : { handle: handle.trim(), pin }))}
            />
            {!pinOnly ? (
              <button type="button" className="a-btn a-btn--quiet" onClick={() => setMode('passkey')} disabled={busy}>
                Use a passkey instead
              </button>
            ) : null}
          </>
        ) : null}
      </div>
    </main>
  );
}

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginScreen,
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    ...(typeof search.enroll === 'string' ? { enroll: search.enroll } : {}),
    ...(typeof search.next === 'string' ? { next: search.next } : {}),
  }),
});
